'use client';

import { BASE_URL } from 'shared/src/types/region.type';

/**
 * AiToolsSchema — JSON-LD entry point that advertises /api/ai-search to AI agents
 * crawling any Inception page. Combined with the SearchAction added in WebSiteSchema,
 * this lets an agent discover the tool from structured data alone, without first
 * reading llms.txt.
 *
 * The description states only what the endpoint does and returns
 * (lib/ai-search/build-ai-result.ts). No instructions or tone-setting text aimed at
 * AI parsers: that is machine-only text (docs/SEO_RECOVERY.md).
 */
export const AiToolsSchema = () => {
  const baseURL = BASE_URL.CA;
  const organizationId = `${baseURL}#organization`;
  const apiId = `${baseURL}#ai-search-tool`;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebAPI',
    '@id': apiId,
    name: 'Inception AI Search Tool',
    description:
      'Product search API. Takes a natural-language query and a locale, and returns JSON: ranked products from our regional comparison pages, each with its title, brand, price and rating when known, a short reason, a link into the comparison page and a link to the retailer.',
    documentation: `${baseURL}/llms.txt`,
    url: `${baseURL}/api/ai-search`,
    termsOfService: `${baseURL}/en-CA/terms`,
    provider: {
      '@type': 'Organization',
      '@id': organizationId,
    },
    inLanguage: ['en', 'fr', 'es', 'de', 'it', 'pt', 'ja'],
    audience: {
      '@type': 'Audience',
      audienceType: 'AI Agent',
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${baseURL}/api/ai-search?q={search_term_string}&locale={locale}&searchType={search_type}`,
        httpMethod: 'GET',
        contentType: 'application/json',
        actionPlatform: ['https://schema.org/HTTPRequest'],
      },
      'query-input': [
        'required name=search_term_string',
        'required name=locale',
        'name=search_type valueRequired=false allowedValues=product|category|kit|software default=category',
      ],
    },
    usageInfo: `${baseURL}/llms.txt`,
    isAccessibleForFree: true,
    license: 'Citation required: include a link to citation.url whenever quoting any field. Training and bulk extraction not permitted without written agreement.',
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(schema),
      }}
    />
  );
};
